import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import api from '../../../../../api/axios';
import StandingProgress from './StandingProgress';
import StandingStats from './StandingStats';
import ExcellentRequirements from './ExcellentRequirements';
import StandingConsequences from './StandingConsequences';
import PunishmentTable from './PunishmentTable';
import RestrictionHistory from './RestrictionHistory';

export type StandingLevel = 'EXCELLENT' | 'GOOD' | 'POOR' | 'AT_RISK' | 'BANNED';

export type RestrictionEnforcementType = 'NORMAL' | 'EXCELLENT_PROTECTION';

export interface UserRestrictionDetailsDTO {
    id: string;
    points: number;
    protectedPoints: number;
    remainingPoints: number;
    enforcementType: RestrictionEnforcementType;
    creationDate: string;
    removedAt: string | null;
}

export interface UserRestrictionDTO {
    standing: StandingLevel;
    activeRestrictionPoints: number;
    excellentProtectionRemainingPoints: number;
    excellentProtectionUsedPoints: number;
    excellentProtectionTotalPoints: number;
    lifetimeTradeCount: number;
    recentTradeCount: number;
    requiredLifetimeTradeCount: number;
    requiredRecentTradeCount: number;
    hasRecentRelevantRestriction: boolean;
    excellentCandidate: boolean;
    excellentEligible: boolean;
    lastRestrictionDecayAt: string | null;
    nextRestrictionDecayAt: string | null;
    restrictionDecayPoints: number;
    restrictionDecayAvailable: boolean;
    restrictions: UserRestrictionDetailsDTO[];
}

export interface StandingStep {
    level: StandingLevel;
    label: string;
    icon: React.ReactNode;
    color: string;
}

export interface StandingConsequence {
    level: StandingLevel;
    penalty: string;
    consequence: string;
}

interface AccountStandingProps {
    onLoaded: () => void;
}

const AccountStanding = ({onLoaded}: AccountStandingProps) => {
    const {t} = useTranslation();
    const [data, setData] = useState<UserRestrictionDTO | null>(null);

    useEffect(() => {
        const fetchRestrictions = async () => {
            try {
                const response = await api.get<UserRestrictionDTO>('/user/restrictions');
                setData(response.data);
            } catch (error) {
                console.error('Error fetching restrictions:', error);
            } finally {
                onLoaded();
            }
        };

        fetchRestrictions();
    }, [onLoaded]);

    if (!data) return null;

    const getStandingColor = (level: StandingLevel) => {
        switch (level) {
            case 'EXCELLENT':
                return 'text-emerald-500';
            case 'GOOD':
                return 'text-blue-500';
            case 'POOR':
                return 'text-amber-500';
            case 'AT_RISK':
                return 'text-orange-600';
            case 'BANNED':
                return 'text-red-600';
            default:
                return 'text-gray-500';
        }
    };

    const getStandingBackground = (level: StandingLevel) => {
        switch (level) {
            case 'EXCELLENT':
                return 'bg-emerald-500/10 border-emerald-500/20';
            case 'GOOD':
                return 'bg-blue-500/10 border-blue-500/20';
            case 'POOR':
                return 'bg-amber-500/10 border-amber-500/20';
            case 'AT_RISK':
                return 'bg-orange-500/10 border-orange-500/20';
            case 'BANNED':
                return 'bg-red-500/10 border-red-500/20';
            default:
                return 'bg-white/5 border-white/5';
        }
    };

    const getPenaltyByLevel = (level: StandingLevel) => {
        switch (level) {
            case 'EXCELLENT':
                return t('dashboard.profile.standing.penalty_pardon');
            case 'GOOD':
                return t('dashboard.profile.standing.penalty_1day');
            case 'POOR':
                return t('dashboard.profile.standing.penalty_3days');
            case 'AT_RISK':
                return t('dashboard.profile.standing.penalty_ban');
            case 'BANNED':
                return t('dashboard.profile.standing.penalty_is_banned');
            default:
                return '';
        }
    };

    const steps: StandingStep[] = [
        {
            level: 'EXCELLENT',
            label: t('dashboard.profile.standing.excellent'),
            icon: <span className="text-lg">✓</span>,
            color: 'text-emerald-500'
        },
        {
            level: 'GOOD',
            label: t('dashboard.profile.standing.good'),
            icon: <span className="text-lg">👍</span>,
            color: 'text-blue-500'
        },
        {
            level: 'POOR',
            label: t('dashboard.profile.standing.poor'),
            icon: <span className="text-lg">◷</span>,
            color: 'text-amber-500'
        },
        {
            level: 'AT_RISK',
            label: t('dashboard.profile.standing.at_risk'),
            icon: <span className="text-lg">!</span>,
            color: 'text-orange-600'
        },
        {
            level: 'BANNED',
            label: t('dashboard.profile.standing.banned'),
            icon: <span className="text-lg">×</span>,
            color: 'text-red-600'
        }
    ];

    return (
        <section className="max-w-[1600px] mx-auto px-4 md:px-6 py-4 font-sans text-light-text dark:text-dark-text">
            <div className="bg-neutral-50 dark:bg-neutral-900/20 border border-neutral-200 dark:border-white/5 rounded-xl shadow-xl p-5 md:p-6 backdrop-blur-md">

                <div className="flex flex-col md:flex-row justify-between items-start gap-3 mb-8">
                    <div>
                        <h3 className="text-2xl font-black tracking-tight uppercase">
                            {t('dashboard.profile.standing.title')}
                        </h3>

                        <p className="text-[12px] font-bold text-gray-400 dark:text-slate-500 mt-1">
                            {t(`dashboard.profile.standing.status.${data.standing.toLowerCase()}`)}
                        </p>
                    </div>

                    <div className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${getStandingBackground(data.standing)} ${getStandingColor(data.standing)}`}>
                        {t(`dashboard.profile.standing.${data.standing.toLowerCase()}`)}
                    </div>
                </div>

                <StandingProgress
                    data={data}
                    steps={steps}
                />

                <StandingStats data={data}/>

                <ExcellentRequirements data={data}/>

                <StandingConsequences
                    data={data}
                    getStandingColor={getStandingColor}
                    getStandingBackground={getStandingBackground}
                    getPenaltyByLevel={getPenaltyByLevel}
                />

                <PunishmentTable/>

                <RestrictionHistory data={data}/>
            </div>
        </section>
    );
};

export default AccountStanding;